import { useMemo } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import type { DsSelectOptions } from '~/components/common/DsSelect'
import { JEPX_AREA_CODES, convertAreaLabel } from '~/models/area'
import { toProtoDate } from '~/models/dateTime'
import type { IntradayFeeTreatment } from '~/pages/IntradayBidding/hooks/useIntradayFeeTreatment'
import {
  type IntradayFormulaPricePreviewInput,
  useIntradayFormulaPricePreview,
} from '~/pages/IntradayBidding/hooks/useIntradayFormulaPricePreview'
import { useIntradayMarketClosed } from '~/pages/IntradayBidding/hooks/useIntradayMarketClosed'
import {
  type FeeAutoConsiderHint,
  feeAutoConsiderHint,
  formatYenPerKwh,
} from '~/pages/IntradayBidding/models/feeAutoConsider'
import {
  FORMULA_OPERATORS,
  FORMULA_VARIABLES,
  type FormulaPricePreview,
  type IntradayFormulaFormValues,
  type IntradayFormulaInput,
  SYSTEM_PRICE_REFERENCE_VALUE,
  formulaOperatorSymbol,
  formulaPreviewScopeLabel,
  formulaReferenceAreaLabel,
  formulaVariableLabel,
  isAdditiveFormulaOperator,
  isIntradayFormulaVariable,
  parseFormulaReferenceArea,
  parseIntradayFormulaFormValues,
  resolveFormulaReferenceAreaValue,
} from '~/pages/IntradayBidding/models/intradayFormula'
import {
  type FormulaPriceSubmitCheck,
  formulaPriceSubmitCheck,
} from '~/pages/IntradayBidding/models/intradayFormulaPricePreview'

type ChoicesParams = {
  isOpen: boolean
  deliveryDate: Date
  values: IntradayFormulaFormValues
}

type Params = {
  isOpen: boolean
  deliveryDate: Date
  values: IntradayFormulaFormValues & {
    priceType: string
    timeCode: string
    side: string
  }
  fee: IntradayFeeTreatment
}

// AC-BIN-005a: 未開場の受渡日では時間前の 3 変数を選べない (選択肢には残す)。
const buildFormulaVariableOptions = (
  t: TFunction,
  marketClosed: boolean,
): DsSelectOptions =>
  FORMULA_VARIABLES.map((v) => ({
    value: v,
    label: formulaVariableLabel(t, v),
    disabled: marketClosed && isIntradayFormulaVariable(v),
  }))

/** フォーミュラ欄の選択肢と、 選択中の名前 (hover の全文表示)。 */
export type IntradayFormulaFieldChoices = {
  variableOptions: DsSelectOptions
  referenceAreaOptions: DsSelectOptions
  /** 参照エリア欄に出す値 (利用者が選ぶまでは札のエリア)。 */
  referenceAreaValue: string
  variableHint: string
  referenceAreaHint: string
  operatorHint: string
  /** オフセット値に単位 (円) を付けるか。 */
  offsetHasYenUnit: boolean
}

/** 計算結果のプレビューの下に出す行 (計算結果・参照した値・入札価格の補足)。 */
export type IntradayFormulaPreviewLines = {
  price: FormulaPricePreview
  /** 計算に使った値と、 値を求めた範囲の補足。 */
  referenceText: string | undefined
  /** 計算結果を希望価格とした入札価格の補足。 入札価格が無い間は出さない。 */
  feeHint: FeeAutoConsiderHint | undefined
}

export type IntradayFormulaFieldsState = IntradayFormulaFieldChoices & {
  preview: ReturnType<typeof useIntradayFormulaPricePreview>
  /** 計算結果を希望価格とした入札価格の補足。 入札価格が無い間は出さない。 */
  feeHint: FeeAutoConsiderHint | undefined
  /** 計算に使った値と、 値を求めた範囲の補足。 */
  referenceText: string | undefined
  /** 作成・保存してよいか。 フォーミュラを選んでいない間は `ok`。 */
  submit: FormulaPriceSubmitCheck
}

/**
 * フォーミュラ欄の選択肢・選択中の名前・参照エリアの追従 (AC-BIN-005 / 005a)。 札作成・入札編集・
 * 一括作成で共通。 計算結果のプレビューは持たない (1 コマの札は `useIntradayFormulaFields`、
 * 一括作成はコマごとに `useIntradayBulkFormulaPreviews` が持つ)。
 */
export function useIntradayFormulaFieldChoices({
  isOpen,
  deliveryDate,
  values,
}: ChoicesParams): IntradayFormulaFieldChoices {
  const { t } = useTranslation()
  const marketClosed = useIntradayMarketClosed(deliveryDate, isOpen)

  const variableOptions = useMemo(
    () => buildFormulaVariableOptions(t, marketClosed),
    [t, marketClosed],
  )
  // 9 エリア。 変数がスポット約定の時だけシステムプライスも選べる。
  const referenceAreaOptions: DsSelectOptions = useMemo(
    () => [
      ...JEPX_AREA_CODES.map((code) => ({
        value: String(code),
        label: convertAreaLabel(code),
      })),
      ...(values.formulaVariable === 'spot'
        ? [
            {
              value: SYSTEM_PRICE_REFERENCE_VALUE,
              label: t('page:biddingIntraday.form.formulaReferenceSystemPrice'),
            },
          ]
        : []),
    ],
    [values.formulaVariable, t],
  )

  // 参照エリア欄に出す値。 利用者が選ぶまでは札のエリア。
  const referenceAreaValue = resolveFormulaReferenceAreaValue(
    values.formulaReferenceArea,
    values.areaCode,
  )

  // 選択中の名前 (未選択は欄名)。 hover の全文表示に使う。
  const selectedVariable = FORMULA_VARIABLES.find(
    (v) => v === values.formulaVariable,
  )
  const variableHint =
    selectedVariable !== undefined
      ? formulaVariableLabel(t, selectedVariable)
      : t('page:biddingIntraday.form.formulaVariable')
  const selectedReferenceArea = parseFormulaReferenceArea(referenceAreaValue)
  const referenceAreaHint =
    selectedReferenceArea !== undefined
      ? formulaReferenceAreaLabel(t, selectedReferenceArea)
      : t('page:biddingIntraday.form.formulaReferenceArea')
  const selectedOperator = FORMULA_OPERATORS.find(
    (op) => op === values.formulaOperator,
  )
  const operatorHint =
    selectedOperator !== undefined
      ? formulaOperatorSymbol(selectedOperator)
      : t('page:biddingIntraday.form.formulaOperator')

  return {
    variableOptions,
    referenceAreaOptions,
    referenceAreaValue,
    variableHint,
    referenceAreaHint,
    operatorHint,
    // × / ÷ のオフセット値は倍率なので単位 (円) を付けない。
    offsetHasYenUnit: isAdditiveFormulaOperator(values.formulaOperator),
  }
}

/**
 * 1 コマの札 (札作成・入札編集) のフォーミュラ欄の状態 (AC-BIN-005 / 005a / 005c)。 選択肢に加え、
 * backend のプレビューと、 そこから出す補足表示を持つ。
 * 計算は backend のプレビュー RPC の結果を出すだけで、 frontend では計算しない。
 */
export function useIntradayFormulaFields({
  isOpen,
  deliveryDate,
  values,
  fee,
}: Params): IntradayFormulaFieldsState {
  const { t } = useTranslation()
  const choices = useIntradayFormulaFieldChoices({
    isOpen,
    deliveryDate,
    values,
  })
  const deliveryProtoDate = useMemo(
    () => toProtoDate(deliveryDate),
    [deliveryDate],
  )

  const input: IntradayFormulaInput | undefined =
    values.priceType === 'formula'
      ? parseIntradayFormulaFormValues(values)
      : undefined
  const koma = Number(values.timeCode)
  const previewInput: IntradayFormulaPricePreviewInput | undefined =
    input !== undefined &&
    Number.isInteger(koma) &&
    koma >= 1 &&
    koma <= 48 &&
    values.areaCode !== '' &&
    (values.side === '1' || values.side === '2')
      ? {
          targetDate: deliveryProtoDate,
          timeCode: koma,
          areaCode: Number(values.areaCode) as AreaCode,
          direction: values.side === '2' ? Side.SELL : Side.BUY,
          formula: input,
        }
      : undefined
  const preview = useIntradayFormulaPricePreview(previewInput, fee)

  // 取得不可・計算できない・プレビュー失敗の時は入札価格が無いので、 手数料の補足 (入力待ちの案内等) を出さない。
  // OFF として扱う間の案内は入札価格の有無によらず出す (AC-BIN-004b)。
  const feeHint =
    preview.fee.kind !== 'fallbackOff' &&
    (preview.price.kind === 'variableUnavailable' ||
      preview.price.kind === 'notCalculable' ||
      preview.price.kind === 'failed')
      ? undefined
      : feeAutoConsiderHint(preview.fee)
  const reference =
    preview.price.kind === 'calculated' ||
    preview.price.kind === 'notCalculable'
      ? preview.price
      : undefined
  const referenceText =
    input !== undefined &&
    reference?.referencePriceYenPerKwh !== undefined &&
    reference.referenceScope !== undefined
      ? t('page:biddingIntraday.form.formulaReferenceValue', {
          variable: formulaVariableLabel(t, input.variable),
          area: formulaPreviewScopeLabel(t, reference.referenceScope),
          price: formatYenPerKwh(reference.referencePriceYenPerKwh),
        })
      : undefined

  return {
    ...choices,
    preview,
    feeHint,
    referenceText,
    submit:
      values.priceType === 'formula'
        ? formulaPriceSubmitCheck(preview)
        : { kind: 'ok' },
  }
}
